/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { css } from 'aphrodite';
import * as React from 'react';

import { Text } from '@blend/enterprise-components';

import { ClosingScheduleStatus } from './utils';
import { cardWrapperStyles } from '../SectionCard';
import {
  ScheduledClosingAppointmentSection,
  ScheduledV2ClosingAppointmentSection,
  UnscheduledClosingAppointmentSection,
  UnscheduledV2ClosingAppointmentSection,
} from './ClosingAppointmentSection';
import * as _ from 'lodash';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

const isClosingScheduled = (schedule?: EClose.API.ScheduleResponse): boolean =>
  schedule?.status === ClosingScheduleStatus.CONFIRMED;
const isScheduleActive = (schedule?: EClose.API.ScheduleResponse): boolean =>
  schedule?.status === ClosingScheduleStatus.ACTIVE || schedule?.status === ClosingScheduleStatus.CONFIRMED;
const isClosingScheduledWithMultiSchedule = (schedules?: EClose.API.ScheduleResponse[]): boolean =>
  !!schedules?.find(isClosingScheduled);
const hasAnActiveSchedule = (schedules?: EClose.API.ScheduleResponse[]): boolean =>
  !!schedules?.find(
    schedule =>
      schedule?.status === ClosingScheduleStatus.ACTIVE || schedule?.status === ClosingScheduleStatus.CONFIRMED,
  );

/**
 * Renders the messaging that the schedule  is pending completion
 * @return {ReactNode} Container of the messaging
 * @function
 */
const renderPendingMessaging = (): React.ReactNode => {
  return (
    <div className={css(cardWrapperStyles.singleCardWrapper)}>
      <Text>Closing appointment information will appear here once ready.</Text>
    </div>
  );
};

/**
 * Renders Closing Appointment Card
 * @param  {EClose.FullClosing} closing The FullClosing data of the Schedule
 * @return {ReactNode}                  The container of the ClosingAppointmentCard
 * @function
 */
const renderClosingAppointment = (closing: EClose.FullClosing): React.ReactNode => {
  return (
    <div className={css(cardWrapperStyles.singleCardWrapper)}>
      <ClosingAppointmentCard closing={closing} />
    </div>
  );
};

const renderAllClosingAppointments = (
  closing: EClose.FullClosing,
  isMultipleSchedulingEnabledOnBridge?: boolean,
): React.ReactNode => {
  const { schedules } = closing;
  let closingAppointmentCards;
  if (isMultipleSchedulingEnabledOnBridge) {
    closingAppointmentCards = _.map(schedules, (schedule: EClose.API.ScheduleResponse) => {
      return (
        <div className={css(cardWrapperStyles.singleCardWrapper)}>
          <ClosingAppointmentMultiScheduleCard closing={closing} schedule={schedule} />
        </div>
      );
    });
  } else {
    closingAppointmentCards = renderClosingAppointment(closing);
  }
  return <div>{closingAppointmentCards}</div>;
};

export const getClosingAppointmentConfig = (
  closing: EClose.FullClosing | undefined,
  isMultipleSchedulingEnabledOnBridge?: boolean,
): DashboardSectionItem.SectionConfig | undefined => {
  if (!closing?.schedule && !closing?.schedules) return;

  const sectionTitle = 'Closing Appointment Section';
  const pendingSchedulingLabel = 'Pending scheduling';
  const pendingAppointmentConfig: DashboardSectionItem.SectionConfig = {
    content: renderPendingMessaging(),
    pill: {
      text: pendingSchedulingLabel,
      theme: 'warning',
    },
    title: sectionTitle,
  };
  let hasClosingBeenScheduled;

  // if no schedules are `ACTIVE` render a pending flag
  if (isMultipleSchedulingEnabledOnBridge) {
    if (!hasAnActiveSchedule(closing.schedules)) return pendingAppointmentConfig;
    hasClosingBeenScheduled = isClosingScheduledWithMultiSchedule(closing.schedules);
  } else {
    if (!isScheduleActive(closing.schedule)) return pendingAppointmentConfig;
    hasClosingBeenScheduled = isClosingScheduled(closing.schedule);
  }

  return {
    content: renderAllClosingAppointments(closing, isMultipleSchedulingEnabledOnBridge),
    pill: {
      text: hasClosingBeenScheduled ? `Scheduled` : pendingSchedulingLabel,
      theme: hasClosingBeenScheduled ? 'default' : 'warning',
    },
    title: sectionTitle,
  };
};

export namespace ClosingAppointmentCard {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    closing: EClose.FullClosing;
  }
}

export namespace ClosingAppointmentMultiScheduleCard {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    closing: EClose.FullClosing;
    schedule: EClose.API.ScheduleResponse;
  }
}

export const ClosingAppointmentCard: React.FC<ClosingAppointmentCard.Props> = ({ closing }) => {
  if (!isScheduleActive(closing.schedule)) {
    return null;
  }

  if (isClosingScheduled(closing.schedule)) {
    // schedule is `ACTIVE` and has times selected
    return <ScheduledClosingAppointmentSection closing={closing} />;
  }

  // schedule is `ACTIVE` and does not have times selected
  return <UnscheduledClosingAppointmentSection closing={closing} />;
};

export const ClosingAppointmentMultiScheduleCard: React.FC<ClosingAppointmentMultiScheduleCard.Props> = ({
  closing,
  schedule,
}) => {
  if (!isScheduleActive(schedule)) {
    return null;
  }

  if (isClosingScheduled(schedule)) {
    // schedule is `ACTIVE` and has times selected
    return <ScheduledV2ClosingAppointmentSection closing={closing} schedule={schedule} />;
  }

  // schedule is `ACTIVE` and does not have times selected
  return <UnscheduledV2ClosingAppointmentSection closing={closing} schedule={schedule} />;
};
